import { useGet } from 'hooks';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  exportTemplateActions as actions,
  exportTemplateSelectors as selectors,
} from 'reducers/exportTemplate/reducer';
import { calculateDate } from 'components/Search/helpers';
import { exportTemplatesSearch } from 'services/api';
import { errorResToMessageArray } from './errorResToMessageArray';
import { CloseCircleOutlined } from '@ant-design/icons';
import { notification } from 'antd';

function getLocalDateString(dateStr) {
  const date = new Date(dateStr);
  const year = date.getFullYear(); // local year
  const month = String(date.getMonth() + 1).padStart(2, '0'); // local month
  const day = String(date.getDate()).padStart(2, '0'); // local day
  return `${year}-${month}-${day}T00:00:00.000Z`; // "2026-05-11T00:00:00.000Z"
}

/** @typedef {import('reducers/exportTemplate/state').SearchExportTemplateListItem} SearchExportTemplateListItem */
/** @typedef {import('reducers/exportTemplate/state').SearchExportTemplatesResponse} SearchExportTemplatesResponse */

/**
 * Hook to fetch and manage export template list with filtering, sorting, and pagination.
 * @returns {{ templates: SearchExportTemplateListItem[], loading: boolean, totalCount: number, refresh: Function }}
 */
export const useExportTemplateList = () => {
  const searchConfig = useSelector(selectors.getSearchConfiguration);
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const onRequestError = useCallback(res => {
    notification.open({
      icon: <CloseCircleOutlined style={{ fontSize: 20, color: 'red' }} />,
      message: 'Error',
      description: (
        <div>
          Failed to fetch Export Templates.
          {errorResToMessageArray(res).map(err => (
            <p key={err} style={{ fontSize: 12, color: '#666' }}>
              {err}
            </p>
          ))}
        </div>
      ),
      placement: 'bottomLeft',
      duration: 8, // Seconds
    });
  }, []);

  const [{ loading }, fetchTemplates] = useGet({
    url: exportTemplatesSearch,
    onMount: false,
    configuration: {
      /** @param {SearchExportTemplatesResponse} res */
      onRequestSuccess: res => {
        setTotalCount(res?.totalCount || 0);

        // Set favourite template id if it exists
        if (res?.favouriteId) {
          dispatch(actions.favouriteTemplateSet(res?.favouriteId));
        }

        const responseTemplates = res?.data || [];
        if (!Array.isArray(responseTemplates)) return;

        setTemplates(responseTemplates);
      },
      onRequestError,
    },
  });

  // Fetch templates when search config changes
  useEffect(() => {
    const dateOption = searchConfig.searchDateOption;
    const isCustom = dateOption === 'custom';
    const dateSource = isCustom ? searchConfig.searchDateRange : dateOption;
    const [startDate, endDate] = dateSource ? calculateDate(dateSource) : [];

    const query = Object.fromEntries(
      Object.entries({
        searchFilter: searchConfig.searchFilter || undefined,
        startDate: startDate ? getLocalDateString(startDate) : undefined,
        endDate: endDate ? getLocalDateString(endDate) : undefined,
        pageSize: searchConfig.pageSize,
        pageNumber: searchConfig.pageNumber,
        orderBy: searchConfig.orderBy,
        order: searchConfig.order,
      }).filter(([, v]) => v !== undefined)
    );

    fetchTemplates({ query });
  }, [
    fetchTemplates,
    searchConfig.searchFilter,
    searchConfig.searchDateOption,
    searchConfig.searchDateRange,
    searchConfig.pageSize,
    searchConfig.pageNumber,
    searchConfig.orderBy,
    searchConfig.order,
    searchConfig.mutationCount, // Trigger refresh when mutation count changes
  ]);

  return {
    templates,
    loading,
    totalCount,
    refresh: fetchTemplates,
  };
};
